/**
 * Smoothly scrolls the window to the target position with optimized acceleration/deceleration
 *
 * @param targetPosition The target scroll position (0 for top of page)
 * @param duration The duration of the scroll animation in milliseconds
 * @param easing The easing function to use for the animation (default: easeOutCubic)
 */
export const smoothScrollTo = (
  targetPosition: number = 0,
  duration: number = 800,
  easing:
    | "easeOutCubic"
    | "easeOutQuint"
    | "easeInOutQuad"
    | "easeInOutCubic"
    | "easeInOutSine"
    | "easeOutExpo" = "easeOutCubic"
): void => {
  // If browser supports ScrollToOptions with behavior: 'smooth'
  const supportsNativeSmoothScroll =
    "scrollBehavior" in document.documentElement.style;

  // For very short distances, use instant scroll to avoid unnecessary animations
  if (Math.abs(window.scrollY - targetPosition) < 50) {
    window.scrollTo(0, targetPosition);
    return;
  }

  // Use native smooth scrolling if available and we're on desktop
  // (native smooth scrolling can be jerky on some mobile devices)
  if (supportsNativeSmoothScroll && window.innerWidth >= 768 && false) {
    // Disabled for now to use custom implementation
    window.scrollTo({
      top: targetPosition,
      behavior: "smooth",
    });
    return;
  }

  // Otherwise use our custom implementation
  // Get current scroll position
  const startPosition = window.scrollY;
  // Calculate distance to scroll
  const distance = targetPosition - startPosition;

  // Return if already at target position or no distance to scroll
  if (distance === 0) return;

  // Time when animation started
  const startTime = performance.now();

  // Enhanced animation easing functions
  const easingFunctions = {
    // Cubic ease-out - good general purpose decreasing velocity
    easeOutCubic: (t: number): number => 1 - Math.pow(1 - t, 3),

    // Quintic ease-out - more pronounced deceleration
    easeOutQuint: (t: number): number => 1 - Math.pow(1 - t, 5),

    // Quadratic ease-in-out - smoother start and end
    easeInOutQuad: (t: number): number =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,

    // Cubic ease-in-out - smoother acceleration and deceleration
    easeInOutCubic: (t: number): number =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,

    // Sinusoidal ease-in-out - gentle natural motion
    easeInOutSine: (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2,

    // Exponential ease-out - dramatic deceleration
    easeOutExpo: (t: number): number =>
      t === 1 ? 1 : 1 - Math.pow(2, -10 * t),
  };

  // Select the appropriate easing function
  const easingFunction = easingFunctions[easing];

  // Animation frame function with optimized rendering
  const animateScroll = (currentTime: number) => {
    // Calculate elapsed time
    const elapsedTime = currentTime - startTime;
    // Calculate progress ratio (0 to 1)
    const progress = Math.min(elapsedTime / duration, 1);
    // Apply easing function for decreasing velocity effect
    const easedProgress = easingFunction(progress);

    // Calculate new position and scroll to it
    const newPosition = Math.round(startPosition + distance * easedProgress);
    window.scrollTo(0, newPosition);

    // Continue animation if not finished
    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    } else {
      // Ensure we land precisely on the target position when animation completes
      // This prevents the jerking effect caused by rounding errors or DOM reflows
      window.scrollTo(0, targetPosition);
    }
  };

  // Start animation
  requestAnimationFrame(animateScroll);
};
